import React from 'react';

import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import FormContainer from '../../../../FormContainer';
import Spinner from '../../../../../Spinner';

import { useUiContext } from '../../../../../../context/UiContext';
import ServicerService from '../../../../../../services/ServicerService';
import UploadService from '../../../../../../services/UploadService';
import useServicerForm from '../../../../../../forms/admin/servicer';

const checkFileAndUpload = async (file) => {
  if (!file) {
    return '';
  }

  if (file.url) {
    return file;
  }

  const response = await UploadService.uploadImage(file);
  return response;
};

const createFormValuesWithImages = async (formValues) => {
  const [logo, logoSecondary] = await Promise.all([
    checkFileAndUpload(formValues.logo),
    checkFileAndUpload(formValues.logo_secondary),
  ]);

  return {
    ...formValues,
    logo,
    logo_secondary: logoSecondary,
  };
};

export default function Servicer({ id, servicer }) {
  const { showToast } = useUiContext();

  const navigate = useNavigate();

  const { mutate: createServicer, isPending: isCreatingServicer } = useMutation(
    {
      mutationFn: async (formValues) => {
        const formValuesWithImages =
          await createFormValuesWithImages(formValues);

        return ServicerService.createServicer({ ...formValuesWithImages });
      },
      onSuccess: (service) => {
        showToast({ type: 'success', text: 'Servicer created' });
        navigate(`/admin/servicers/${service.id}?tab=Contacts`);
      },
      onError: (error) => {
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    }
  );

  const { mutate: updateServicer, isPending: isUpdatingServicer } = useMutation(
    {
      mutationFn: async (formValues) => {
        const formValuesWithImages =
          await createFormValuesWithImages(formValues);

        return ServicerService.updateServicer({
          id,
          formValues: formValuesWithImages,
        });
      },
      onSuccess: () => {
        showToast({ type: 'success', text: 'Servicer updated' });
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    }
  );

  const servicerForm = useServicerForm(
    id ? updateServicer : createServicer,
    servicer
  );

  const isLoading = isCreatingServicer || isUpdatingServicer;

  return (
    <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      <FormContainer form={servicerForm} disabled={isLoading} />
      {isLoading && <Spinner />}
    </div>
  );
}
